import moment from 'moment'

// One balance adjustment, recorded on the order it is for. The order carries a
// list of these; see src/pos/retailOrder/db/awards for the state machine they
// encode. In short:
//
//   adjustingAt           due (or claimed until adjustingAt passes)
//   adjustedAt            applied
//   neither timestamp     given up -- needs a human
//
// `adjustingAt` is the next time we may try, not the time we started, so the
// sweep query is a plain `{ $lte: now }` for both a first attempt and a retry.
// Mongo's comparison operators are type-bracketed, so that predicate matches
// only Dates -- the done and given-up rows (null) fall out for free.
//
// THE ARRAY IS APPEND-ONLY AND ITS INDICES ARE IDENTITIES. An award's position
// is half of the reference written into the provider's history
// (`A:RO260816001:1`), which we cannot edit afterwards. Elements may be added
// and their state fields updated; they may never be reordered or removed.
const award = {
  type: 'Object',
  contents: {
    // Which balance this moves: which history it lands in, and which endpoint
    // sends it.
    type: { type: 'String', label: '異動類別', options: ['point', 'credit'] },
    // Which box the staff typed it into, and so what they meant by it.
    //
    // NOT DERIVABLE FROM THE SIGN, which is why it is stored. A return is
    // entered as a new order with negative quantities, so 贈送點數 -12 is a
    // clawback -- and it stores the identical `{ point, -12 }` as 折抵點數 12.
    // The provider does not care, both being the same signed delta, but a
    // person reading the order back does.
    intent: { type: 'String', label: '異動方向', options: ['earn', 'spend'] },
    // The signed delta, which is what goes on the wire.
    value: { type: 'Number', label: '異動值' },
    // Free text the staff typed at the till. It BECOMES the reason on the remark
    // we send to the provider, standing in for the 門市消費 / 門市退貨 that remark
    // would otherwise carry. Written once with `value` and never editable
    // afterwards, for the same reason the value is not: it has already been sent.
    //
    // Only the first nine or so characters survive the provider's 50-byte remark
    // limit -- see MAX_NOTE_BYTES in upstreams/shopline/remarks.
    note: { type: 'String', label: '備註' },
    // WHETHER A PERSON OVERRODE THE SUGGESTED VALUE, frozen with the value it
    // describes.
    //
    // ON THE AWARD, NOT ON THE ORDER, and not a map keyed by vendor either.
    // This array can carry several earn entries for one platform, which a keyed
    // map cannot represent at all -- and once there is a second platform, a
    // staffer may well override the figure for one and take the suggestion for
    // the other. The flag is a fact about ONE value, so it lives beside that
    // value, for the same reason `intent` does.
    //
    // NULL WHERE THE QUESTION DOES NOT APPLY: only a box with a suggestion can
    // be overridden, so only 贈送點數 sets this. A 折抵 is always typed and
    // carries null rather than a vacuous false. See `forcePath` in ../awardTypes.
    force: { type: 'Boolean', label: '強制填寫' },

    // WHO THIS WAS FOR, frozen at the till. Read back from here rather than
    // from the consumer, because the sweep that finishes an award hours later
    // must send to whoever it named then -- not to whoever the phone resolves
    // to by the time it runs.
    vendor: { type: 'String', label: '平台' },
    vendorId: { type: 'String', label: '平台會員編號' },
    memberRef: { type: 'ObjectId', label: '會員', reference: 'members' },

    adjustingAt: {
      type: 'Date',
      label: '下次嘗試時間',
      format: 'YYYY/MM/DD HH:mm',
    },
    adjustedAt: { type: 'Date', label: '完成時間', format: 'YYYY/MM/DD HH:mm' },
    // Diagnostic only, never read to decide what to do next.
    error: { type: 'String', label: '失敗原因' },
  },
}

// What the member had when the phone number was typed.
//
// A SNAPSHOT, NEVER A SOURCE OF TRUTH. Read live so the staff can tell the
// customer how much there is to spend, and stored only as a record of what was
// on screen at the time -- the same customer also buys online, so it is stale
// the moment it is written. Nothing reads it back to decide anything; awards
// carry their own copy of vendor and vendorId.
//
// Rewritten every time a phone resolves, which is why it lives under `consumer`
// rather than beside the award log.
//
// KEYED BY VENDOR, NOT AN ARRAY, and the key IS the vendor -- so there is no
// `vendor` field inside. Three things follow, and all of them matter:
//
//   * one row per platform is structural rather than a convention
//   * a form path can name it: `consumer.members.shopline.vendorId`. coact
//     builds its field dictionary by traversing this file and treats an array
//     node as a LEAF, so no `members.0.vendorId` path is ever in that dictionary
//     and such a field would render with no label and no type
//   * the vendors are enumerable from the schema, which is what would let the
//     page generate one section per vendor instead of listing them
// Labels carry no vendor name: each vendor has its own titled section on the
// till, so 「SHOPLINE 資訊」 already says which platform these belong to, and
// repeating it only makes the widest column in the row wider still.
const membership = {
  type: 'Object',
  contents: {
    vendorId: { type: 'String', label: '會員編號' },
    memberRef: { type: 'ObjectId', label: '會員', reference: 'members' },
    // null means the platform did not report a figure, which is NOT zero -- at
    // a till those mean opposite things.
    points: { type: 'Number', label: '目前點數' },
    credits: { type: 'Number', label: '目前購物金' },
  },
}

const header = {
  type: 'Object',
  contents: {
    _id: { type: 'ObjectId' },
    key: {
      type: 'String',
      label: '零售單號',
      counter: {
        key: () => `RO${moment().format('YYMMDD')}`,
        value: (key, value) => `${key}${value.toString().padStart(3, 0)}`,
      },
    },
    date: { type: 'Date', label: '日期', format: 'YYYY/MM/DD HH:mm' },
    status: {
      type: 'String',
      label: '狀態',
      options: ['新增', '完成', '取消'],
    },
    warehouse: {
      type: 'Object',
      contents: {
        id: {
          type: 'ObjectId',
          label: '出庫倉',
          reference: 'warehouses',
          refKeys: ['key'],
        },
        key: { type: 'String', label: '倉庫編號' },
        name: { type: 'String', label: '倉庫名稱' },
      },
    },
    pricebook: {
      type: 'Object',
      contents: {
        id: {
          type: 'ObjectId',
          label: '使用價格表',
          reference: 'pricebooks',
          refKeys: ['key'],
        },
        key: { type: 'String', label: '價格表名稱' },
      },
    },
    // 街口 IS NO LONGER OFFERED, and is still listed. The till's radio group is
    // written by hand in PaymentMethod.jsx and has three buttons, so no new
    // order can choose it -- but orders taken while it was accepted still carry
    // the value.
    //
    // Dropping it from here would make those orders UNSAVEABLE. `options` is not
    // documentation: makeValidator turns it into a validator, and
    // runValidatorsOnSchema walks the whole schema over the whole document, so a
    // field nobody touched is still checked. editSaga throws on the result and
    // the save is abandoned -- including the re-checkout that issues an invoice
    // for an order the service was down for.
    //
    // Remove it the day no order carries it, and not before.
    payType: {
      type: 'String',
      label: '付款方式',
      options: ['現金', '信用卡', 'LINE PAY', '街口'],
    },
    salesPerson: {
      type: 'ObjectId',
      label: '服務人員',
      reference: 'staffers',
      refKeys: ['key'],
    },

    note: { type: 'String', label: '備註' },
    // WHAT THE CUSTOMER PAYS: the item subtotals less the whole-order discount
    // AND less anything redeemed with points or store credit. The cashier page
    // computes it, spread across items[].amount by the same shareAmount, so the
    // 統一發票 declares this same figure -- redemption reduces the invoice.
    //
    // Negative on a corrective order, being the amount refunded. Orders written
    // before 2026-08-22 hold the GROSS total instead: redemption used to leave
    // it untouched.
    amount: { type: 'Number', label: '金額' },
    summary: { type: 'String', label: '數量' },
    discount: { type: 'Number', label: '整單折扣' },

    buyerId: { type: 'String', label: '發票統編' },
    mobileBarcode: { type: 'String', label: '手機載具號碼' },
    donation: { type: 'String', label: '捐贈發票碼' },
    citizenBarcode: { type: 'String', label: '自然人憑證載具' },
    consumer: {
      type: 'Object',
      contents: {
        id: {
          type: 'ObjectId',
          label: '消費者',
          reference: 'consumers',
          refKeys: ['name', 'phone', 'mobileBarcode'],
        },
        phone: { type: 'String', label: '手機' },
        name: { type: 'String', label: '姓名' },
        // One entry per loyalty platform, keyed by vendor. See `membership`.
        // A second vendor is one more key here and nothing else.
        members: {
          type: 'Object',
          contents: {
            shopline: membership,
          },
        },
      },
    },

    // Every balance adjustment this order owes, in the order they were made.
    // Append-only -- see `award`.
    awards: { type: 'Array', contents: [award] },

    // THE TILL'S BOXES ARE NOT HERE, on purpose -- the three values, and the
    // flag saying a staffer overrode the suggested one. They are screen state:
    // awardIntake turns them into `awards` above and strips them before the
    // model layer sees the values, so nothing here reads them back. What the
    // override flag recorded is frozen onto each award as `force`, which is
    // where it can be per-platform and per-value rather than one per order.
    //
    // Declaring them here did not make them stored data -- it made them stored
    // NULLS. codev's `complete` gives every optional leaf a `default: null` and
    // autocount's `init` fills it on insert, so every order carried three
    // objects of nothing. They now live on the view schema that actually wants
    // them, beside `invoices`, which has always worked that way: coact builds
    // its field dictionary from the VIEW schema. See src/pos/cashier/schema.
  },
}

const item = {
  type: 'Object',
  contents: {
    _id: { type: 'ObjectId' },
    sku: {
      type: 'Object',
      contents: {
        id: {
          type: 'ObjectId',
          label: '單品',
          reference: 'skus',
          refKeys: ['key'],
        },
        key: { type: 'String', label: '單品編碼' },
        barcode: { type: 'String' },
      },
    },
    barcodeOnObject: { type: 'String', label: '單品上條碼' },
    name: { type: 'String', label: '名稱' },
    qty: { type: 'Number', label: '數量' },
    price: { type: 'Number', label: '單價' },
    subtotal: { type: 'Number', label: '小計' },
    amount: { type: 'Number', label: '計價' },
    note: { type: 'String', label: '項目備註' },
    discountRate: { type: 'Number', label: '折扣', format: '0%' },
    bookPrice: {
      type: 'Object',
      contents: {
        id: { type: 'ObjectId', reference: 'prices' },
        price: { type: 'Number', label: '表價' },
      },
    },
  },
}

export default {
  header,
  item,
}
